import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { streamChat } from "@/lib/aiChat";

const newSession = (key) => { const id = crypto.randomUUID(); sessionStorage.setItem(key, id); return id; };

// Shared multi-turn streaming chat state; history persists per browser tab session
export function useAiChat({ path, historyPath, storageKey, enabled = true }) {
  const [sessionId, setSessionId] = useState(() => sessionStorage.getItem(storageKey) || newSession(storageKey));
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    api.get(`${historyPath}/${sessionId}`, { withCredentials: true })
      .then((r) => setMessages(r.data.messages.map((m) => ({ ...m, id: crypto.randomUUID() })))).catch(() => setMessages([]));
  }, [enabled, historyPath, sessionId]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [messages]);

  const send = useCallback(async (text) => {
    const msg = text.trim();
    if (!msg || busy) return;
    setError(""); setBusy(true);
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", content: msg }, { id: crypto.randomUUID(), role: "assistant", content: "" }]);
    const append = (d) => setMessages((m) => {
      const last = m[m.length - 1];
      return [...m.slice(0, -1), { ...last, content: last.content + d }];
    });
    try {
      await streamChat(path, sessionId, msg, append);
    } catch (e) {
      setError(e.message);
      setMessages((m) => (m[m.length - 1].content ? m : m.slice(0, -1)));
    } finally {
      setBusy(false);
    }
  }, [busy, path, sessionId]);

  const reset = () => { setMessages([]); setError(""); setSessionId(newSession(storageKey)); };
  return { messages, busy, error, send, reset, endRef };
}
